import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import getApiBase from "./getApiBase";
import InviteMechanic from "../pages/InviteMechanic";
import InviteList from "./InviteList";
import { rosterContactLine, rosterRoleLabel } from "../utils/shopRoster";

/**
 * TASK-975 — shop roster on the dashboard. Starts collapsed.
 */
export default function ShopTeamRoster({
  shopId,
  shopStatus,
  currentUserId,
  isOwner,
}) {
  const [expanded, setExpanded] = useState(false);
  const [roster, setRoster] = useState([]);
  const [companyLine, setCompanyLine] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadRoster = async (id) => {
    if (!id) return;
    try {
      const res = await fetch(
        `${getApiBase()}/api/mechanicProfile/shop-mechanics?shop_id=${id}`,
        { credentials: "include" },
      );
      if (res.ok) setRoster(await res.json());
    } catch (err) {
      console.error("Shop roster fetch failed:", err);
    }
  };

  useEffect(() => {
    loadRoster(shopId);
  }, [shopId]);

  useEffect(() => {
    if (!shopId || !isOwner) {
      setCompanyLine(false);
      return;
    }
    const token = localStorage.getItem("token");
    fetch(`${getApiBase()}/api/ezvoice/shops/${shopId}/status`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setCompanyLine(data?.companyLine === true))
      .catch(() => setCompanyLine(false));
  }, [shopId, isOwner]);

  const handlePromoteToOwner = async () => {
    if (!promoteTarget?.id || !shopId) return;
    setPromoting(true);
    try {
      const res = await fetch(`${getApiBase()}/api/mechanicProfile/promote-to-owner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          shop_id: shopId,
          mechanic_id: promoteTarget.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSnackbar({
          open: true,
          message: data.message || "Could not promote teammate",
          severity: "error",
        });
        return;
      }
      setSnackbar({
        open: true,
        message: `${promoteTarget.first_name} is now an Owner`,
        severity: "success",
      });
      setPromoteTarget(null);
      await loadRoster(shopId);
    } catch (err) {
      console.error("Promote to Owner failed:", err);
      setSnackbar({
        open: true,
        message: "Could not promote teammate",
        severity: "error",
      });
    } finally {
      setPromoting(false);
    }
  };

  const handleSetSales = async (mech) => {
    if (!mech?.id || !shopId || !companyLine) return;
    setPromoting(true);
    try {
      const res = await fetch(`${getApiBase()}/api/mechanicProfile/set-sales-role`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          shop_id: shopId,
          mechanic_id: mech.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSnackbar({
          open: true,
          message: data.message || "Could not set Sales",
          severity: "error",
        });
        return;
      }
      setSnackbar({
        open: true,
        message: `${mech.first_name} is now Sales`,
        severity: "success",
      });
      await loadRoster(shopId);
    } catch (err) {
      console.error("Set Sales failed:", err);
      setSnackbar({
        open: true,
        message: "Could not set Sales",
        severity: "error",
      });
    } finally {
      setPromoting(false);
    }
  };

  return (
    <PaperSection>
      <Accordion
        expanded={expanded}
        onChange={(_, next) => setExpanded(next)}
        sx={{ boxShadow: "none", "&:before": { display: "none" } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
          sx={{
            backgroundColor: "#e63926",
            color: "#fff",
            minHeight: 72,
            borderRadius: 2,
            "& .MuiAccordionSummary-content": { margin: "16px 0" },
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            All Mechanics at This Shop
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            {roster.map((mech, i) => (
              <ListItem
                key={mech.id || i}
                divider
                secondaryAction={
                  isOwner &&
                  mech.shop_role === "Mechanic" &&
                  mech.id &&
                  mech.id !== currentUserId ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {companyLine && (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={promoting}
                          onClick={() => handleSetSales(mech)}
                          sx={{ borderColor: "#e63926", color: "#e63926" }}
                        >
                          Set as Sales
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setPromoteTarget(mech)}
                        sx={{ borderColor: "#e63926", color: "#e63926" }}
                      >
                        Promote to Owner
                      </Button>
                    </Box>
                  ) : null
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <span>{`${mech.first_name || ""} ${mech.last_name || ""}`.trim()}</span>
                      <Chip
                        size="small"
                        label={rosterRoleLabel(mech.shop_role)}
                        color={mech.shop_role === "Owner" ? "success" : "default"}
                      />
                    </Box>
                  }
                  secondary={rosterContactLine(mech.email, mech.phone_number)}
                  primaryTypographyProps={{
                    component: "div",
                    sx: { fontSize: "1.1rem", fontWeight: "bold" },
                  }}
                  secondaryTypographyProps={{
                    sx: { fontSize: "1.1rem" },
                  }}
                />
              </ListItem>
            ))}
          </List>
          {isOwner && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => setInviteOpen(true)}
              sx={{
                backgroundColor: "#e63926",
                "&:hover": { backgroundColor: "#d32f2f" },
                py: 2,
                mt: 2,
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              👥 Manage Team & Invitations
            </Button>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={Boolean(promoteTarget)} onClose={() => !promoting && setPromoteTarget(null)}>
        <DialogTitle>Promote to Owner?</DialogTitle>
        <DialogContent>
          <Typography>
            {promoteTarget
              ? `${promoteTarget.first_name} ${promoteTarget.last_name} will become an Owner on this shop and can add a card on Pricing.`
              : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteTarget(null)} disabled={promoting} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button
            onClick={handlePromoteToOwner}
            variant="contained"
            disabled={promoting}
            sx={{ backgroundColor: "#e63926", "&:hover": { backgroundColor: "#d32f2f" } }}
          >
            {promoting ? <CircularProgress size={20} /> : "Promote to Owner"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: "#e63926",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          👥 Manage Team & Invitations
          <IconButton onClick={() => setInviteOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {roster.length >= 10 ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                Team limit reached
              </Typography>
              <Typography variant="body2">
                Maximum of 10 people per shop. Remove someone before inviting another.
              </Typography>
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {10 - roster.length} invitation{10 - roster.length !== 1 ? "s" : ""} remaining (max 10)
                </Typography>
              </Alert>
              <InviteMechanic shopId={shopId} shopStatus={shopStatus} />
            </>
          )}
          <Box mt={3}>
            <InviteList shopId={shopId} />
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PaperSection>
  );
}

function PaperSection({ children }) {
  return (
    <Box
      component="section"
      sx={{
        mb: 4,
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        boxShadow: 2,
        backgroundColor: "#fff",
      }}
    >
      {children}
    </Box>
  );
}
